import { z } from 'zod';
import { LAYER_KEYS, type LayerKey } from './layer-preferences';

/**
 * Feed-health surfacing (#254) — honest "live tracking down since X" chrome.
 *
 * The COP renders a layer's data at whatever freshness the backing ingest daemon
 * last left in the DB. When that daemon DIES, the data silently ages: aircraft
 * derive to the #245 `based` tier, outages freeze at their last scrape, etc. —
 * and the operator sees "everyone is parked" instead of "the feed is dark."
 * `worker_health` already records a heartbeat (`polled_at`) every poll cycle, so
 * a dead daemon is observable: no recent heartbeat for its `worker`. This module
 * is the SINGLE SOURCE OF TRUTH for turning those heartbeats into an honest,
 * layer-scoped down signal, shared by:
 *   - the API (`GET /api/feed-health` returns the raw per-worker heartbeats), and
 *   - the web chrome (`LayerToggle` renders a "feed down" chip per layer).
 *
 * Truth-in-feeds (operator-cop-doctrine): we NEVER fabricate liveness, and — just
 * as importantly — we never fabricate a fault. The down signal fires only when we
 * can truthfully assert a feed is dark, and it is deliberately conservative (it
 * tolerates several missed polls before declaring death) so a single jittered
 * poll never cries wolf. A wrong "down" badge is itself a truth-in-feeds breach.
 */

/** A worker's distilled health: its latest heartbeat + last clean poll. The API
 *  returns one of these per distinct `worker_health.worker`. Timestamps are ISO
 *  strings (postgres-js hands timestamptz back as text). */
export const WorkerHeartbeatSchema = z.object({
  /** The `worker_health.worker` discriminator (e.g. 'aircraft', 'fusion'). */
  worker: z.string(),
  /** Most recent heartbeat of ANY outcome — the liveness signal. null = the
   *  worker has never written a row (never connected, not "went dark"). */
  lastPolledAt: z.string().nullable(),
  /** Most recent heartbeat whose outcome was 'ok' — the last time fresh data
   *  actually landed. Surfaced for the tooltip; the down trigger is heartbeat
   *  recency, not this. */
  lastOkAt: z.string().nullable(),
  /** #640 — most recent heartbeat that WROTE ≥1 row (`records_written > 0`): the
   *  last time this feed actually produced data. The row-count analogue of
   *  `lastOkAt` (which only says the poll SUCCEEDED, not that anything landed).
   *  null = the feed has never written a row. Optional for backward-compat with
   *  cached/older responses; the API always populates it. */
  lastNonZeroAt: z.string().nullable().optional(),
  /** Outcome of the most recent heartbeat (ok | skipped | error), or null. */
  lastOutcome: z.enum(['ok', 'skipped', 'error']).nullable(),
});
export type WorkerHeartbeat = z.infer<typeof WorkerHeartbeatSchema>;

/** `GET /api/feed-health` response. `serverNow` is the DB clock at query time so
 *  the client judges staleness against the server, immune to local clock skew. */
export const FeedHealthResponseSchema = z.object({
  serverNow: z.string(),
  workers: z.array(WorkerHeartbeatSchema),
});
export type FeedHealthResponse = z.infer<typeof FeedHealthResponseSchema>;

/**
 * Worker → layer mapping. Each row ties one `worker_health.worker` to the COP
 * layer its data renders into, with that worker's nominal poll cadence. A layer
 * may be backed by several workers (e.g. Power Outages = every LDC + utility
 * scraper); a worker may back several layers (e.g. CWFIS active fires feeds both
 * reported-wildfire views). The layer is judged DOWN only when EVERY backing
 * worker is stale — so one province's scraper dying never blanks a layer that
 * still has live data elsewhere (`deriveLayerFeedHealth`).
 *
 * Deliberately EXCLUDED (we only badge feeds we can truthfully assess):
 *   - Event/streaming-cadence workers whose quiet ≠ death (marine ingest — R.1d,
 *     a distinct data class from ADS-B; NAAD/CAP — bursty alert stream).
 *   - Client-side WMS overlays with no ingest daemon, hence no heartbeat
 *     (`eccc-hurricane-track`, `nrcan-flood-extent` — upstream gov WMS liveness
 *     is not something we monitor).
 *   - Fusion-derived ESTIMATE layers (`ice-storm-comms-degradation`) — if fusion
 *     dies the Urgent Alerts row already says so; the estimate is labelled
 *     "estimate", not a measured feed.
 *   - Placeholder layers (Air Quality, Earthquakes) with no live render yet, and
 *     non-map workers (sitrep scheduler, media-signal/news pipelines).
 */
export interface FeedWorkerSpec {
  /** The `worker_health.worker` discriminator. */
  worker: string;
  /** The COP layer (LAYER_KEYS) this worker's data renders into. */
  layerKey: LayerKey;
  /** Nominal poll cadence in minutes — the basis for the presumed-dead window. */
  expectedIntervalMinutes: number;
}

/** Satellite thermal pollers all poll ~every 10–15 min. */
const SAT_INTERVAL = 15;
/** Public-ArcGIS feeds + slow gov sources refresh roughly hourly. */
const ARCGIS_INTERVAL = 60;

export const FEED_HEALTH_REGISTRY: ReadonlyArray<FeedWorkerSpec> = [
  // 🎯 Default — the #254 motivating case. ADS-B poll default 900s (15 min). The
  // 'aircraft' layer is "Aircraft & Marine", but marine is a distinct, not-yet-
  // live data class (R.1d) — badging the layer off ADS-B alone is the honest
  // read of "live aircraft tracking is dark".
  { worker: 'aircraft', layerKey: 'aircraft', expectedIntervalMinutes: 15 },

  // 🎯 Urgent Alerts — the AI-fusion producer (poll default 15 min). CAP alerts
  // (naad-cap) co-feed this layer but are a bursty stream, intentionally excluded.
  { worker: 'fusion', layerKey: 'urgent-anomalies', expectedIntervalMinutes: 15 },

  // 🎯 Reported wildfires — CWFIS active-fire ingest backs both reported-fire views.
  { worker: 'cwfis-active-fires', layerKey: 'reported-oc-wildfires', expectedIntervalMinutes: 120 },
  { worker: 'cwfis-active-fires', layerKey: 'all-reported-wildfires', expectedIntervalMinutes: 120 },
  // 🔥 Provincial fire feeds (#603) — BC/AB/ON/QC each back BOTH reported-fire
  // views alongside CWFIS. All-down semantics keep the layer "live" while CWFIS
  // feeds, so ONE province dying never blanks the layer; per-province staleness is
  // surfaced separately (deriveProvincialFireStaleness) so a dark province reads
  // "provincial data unavailable", never "0 fires". Poll cadence 15 min.
  { worker: 'provincial-bc', layerKey: 'reported-oc-wildfires', expectedIntervalMinutes: 15 },
  { worker: 'provincial-bc', layerKey: 'all-reported-wildfires', expectedIntervalMinutes: 15 },
  { worker: 'provincial-ab', layerKey: 'reported-oc-wildfires', expectedIntervalMinutes: 15 },
  { worker: 'provincial-ab', layerKey: 'all-reported-wildfires', expectedIntervalMinutes: 15 },
  { worker: 'provincial-on', layerKey: 'reported-oc-wildfires', expectedIntervalMinutes: 15 },
  { worker: 'provincial-on', layerKey: 'all-reported-wildfires', expectedIntervalMinutes: 15 },
  { worker: 'provincial-qc', layerKey: 'reported-oc-wildfires', expectedIntervalMinutes: 15 },
  { worker: 'provincial-qc', layerKey: 'all-reported-wildfires', expectedIntervalMinutes: 15 },

  // 🔥 Hot Spots — raw pre-fusion satellite thermal (FIRMS / CWFIS M3 / GOES-R /
  // NOAA HMS). All-down: the layer is dark only when every satellite source is.
  { worker: 'firms', layerKey: 'raw-hotspots', expectedIntervalMinutes: SAT_INTERVAL },
  { worker: 'cwfis-m3', layerKey: 'raw-hotspots', expectedIntervalMinutes: 60 },
  { worker: 'goes-r-fdcc', layerKey: 'raw-hotspots', expectedIntervalMinutes: SAT_INTERVAL },
  { worker: 'goes-r-fdcf', layerKey: 'raw-hotspots', expectedIntervalMinutes: SAT_INTERVAL },
  { worker: 'noaa-hms', layerKey: 'raw-hotspots', expectedIntervalMinutes: ARCGIS_INTERVAL },

  // 🔥 Camera Smoke Detections — the camera-CV smoke worker.
  { worker: 'smoke_cv', layerKey: 'camera-smoke-detections', expectedIntervalMinutes: 30 },

  // 🌊 Hydrometric Gauges — WSC river-gauge ingest (~hourly).
  { worker: 'hydrometric-wsc', layerKey: 'hydrometric-gauges', expectedIntervalMinutes: 60 },

  // 🧊 Power Outages — every LDC + utility scraper backs the one Power Outages
  // layer. All-down: badge only when the whole outage picture goes dark.
  { worker: 'ldc-toronto-hydro', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-hydro-ottawa', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-alectra', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-epcor', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-enmax', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-fortisalberta', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-nb-power', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-maritime-electric', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-mb-hydro', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'ldc-saskpower', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'outages-bc-hydro', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'outages-hydro-quebec', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'outages-hydro-one', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },
  { worker: 'outages-ns-power', layerKey: 'ice-storm-power-outages', expectedIntervalMinutes: 15 },

  // 🧊 Road Closures — Ontario 511 / Alberta 511 / DriveBC. All-down.
  { worker: '511on', layerKey: 'ice-storm-road-closures', expectedIntervalMinutes: 15 },
  { worker: '511ab', layerKey: 'ice-storm-road-closures', expectedIntervalMinutes: 15 },
  { worker: 'drivebc', layerKey: 'ice-storm-road-closures', expectedIntervalMinutes: 15 },

  // ⚠️ Evacuation Orders & Alerts — GeoBC / EMBC ArcGIS ingest.
  { worker: 'arcgis:geobc-evac-orders', layerKey: 'bc-evacuation-orders', expectedIntervalMinutes: ARCGIS_INTERVAL },

  // 🔥 Fire Bans & Restrictions — the Alberta fire-restriction ArcGIS sub-feeds.
  { worker: 'arcgis:ab-fire-advisory', layerKey: 'ab-fire-bans', expectedIntervalMinutes: ARCGIS_INTERVAL },
  { worker: 'arcgis:ab-fire-restriction', layerKey: 'ab-fire-bans', expectedIntervalMinutes: ARCGIS_INTERVAL },
  { worker: 'arcgis:ab-fire-ban', layerKey: 'ab-fire-bans', expectedIntervalMinutes: ARCGIS_INTERVAL },
  { worker: 'arcgis:ab-fire-control-order', layerKey: 'ab-fire-bans', expectedIntervalMinutes: ARCGIS_INTERVAL },
  { worker: 'arcgis:ab-forest-area-closure', layerKey: 'ab-fire-bans', expectedIntervalMinutes: ARCGIS_INTERVAL },

  // 📋 Drought Conditions — BC drought basins + MB river/lake flow stations.
  { worker: 'arcgis:geobc-drought', layerKey: 'drought-conditions', expectedIntervalMinutes: ARCGIS_INTERVAL },
  { worker: 'arcgis:mb-drought-river-lake', layerKey: 'drought-conditions', expectedIntervalMinutes: ARCGIS_INTERVAL },

  // 📋 Boil Water Advisories — MB public water systems.
  { worker: 'arcgis:mb-boil-water', layerKey: 'boil-water-advisories', expectedIntervalMinutes: ARCGIS_INTERVAL },

  // 📋 State of Local Emergency — BC SoLE & Band Council Resolutions.
  { worker: 'arcgis:bc-sole', layerKey: 'bc-state-of-local-emergency', expectedIntervalMinutes: ARCGIS_INTERVAL },

  // 📋 Tropical Cyclones — US-NHC forecast position + error cone.
  { worker: 'arcgis:nhc-hurricane-position', layerKey: 'tropical-cyclones', expectedIntervalMinutes: ARCGIS_INTERVAL },
  { worker: 'arcgis:nhc-hurricane-cone', layerKey: 'tropical-cyclones', expectedIntervalMinutes: ARCGIS_INTERVAL },
];

/**
 * How many missed polls we tolerate before declaring a feed dead. 3 = "we have
 * missed three consecutive cycles" — past normal jitter, into genuine death.
 * Conservative on purpose: the cost of a late badge is far lower than the cost
 * of a false one (which would itself break truth-in-feeds).
 */
export const FEED_HEALTH_STALE_MULTIPLIER = 3;

/** Floor on the presumed-dead window, so a hypothetical sub-minute cadence still
 *  needs a real silence before it reads as down. */
export const FEED_HEALTH_MIN_DOWN_AFTER_MS = 10 * 60_000;

/** The silence window (ms) after which a worker on this cadence is presumed dead. */
export function feedHealthDownAfterMs(expectedIntervalMinutes: number): number {
  return Math.max(
    FEED_HEALTH_MIN_DOWN_AFTER_MS,
    expectedIntervalMinutes * 60_000 * FEED_HEALTH_STALE_MULTIPLIER,
  );
}

/** A single worker's liveness against its own cadence.
 *  - 'live'    — heartbeat within the presumed-dead window.
 *  - 'down'    — heartbeat older than the window (daemon presumed dead).
 *  - 'unknown' — no heartbeat ever (never connected; not "went dark"). */
export type FeedHealthState = 'live' | 'down' | 'unknown';

export function workerLiveness(
  lastPolledAtMs: number | null,
  nowMs: number,
  downAfterMs: number,
): FeedHealthState {
  if (lastPolledAtMs === null) return 'unknown';
  return nowMs - lastPolledAtMs > downAfterMs ? 'down' : 'live';
}

/** A layer whose live feed is currently dark (every backing worker stale). */
export interface LayerFeedDown {
  layerKey: LayerKey;
  /** Epoch ms of the most recent heartbeat across the layer's backing workers —
   *  the last moment the layer had ANY live feed ("down since X"). */
  downSinceMs: number;
  /** The backing workers (all stale, by definition of down) — for the tooltip. */
  staleWorkers: string[];
}

/**
 * Derive which COP layers are currently dark from the per-worker heartbeats.
 *
 * A layer is DOWN iff EVERY backing worker is stale (per its own cadence) AND at
 * least one backing worker has actually reported before (so we have a real "since
 * X"). Layers whose backing workers have never reported (all 'unknown') are
 * omitted — we do not claim a fault for a feed that never started. The result
 * maps each down layer to its honest down-since + the backing workers.
 */
export function deriveLayerFeedHealth(
  heartbeats: ReadonlyArray<WorkerHeartbeat>,
  nowMs: number,
): Map<LayerKey, LayerFeedDown> {
  const lastPolledByWorker = new Map<string, number | null>();
  for (const hb of heartbeats) {
    lastPolledByWorker.set(hb.worker, hb.lastPolledAt ? Date.parse(hb.lastPolledAt) : null);
  }

  // Group registry specs by the layer they back.
  const specsByLayer = new Map<LayerKey, FeedWorkerSpec[]>();
  for (const spec of FEED_HEALTH_REGISTRY) {
    const list = specsByLayer.get(spec.layerKey) ?? [];
    list.push(spec);
    specsByLayer.set(spec.layerKey, list);
  }

  const down = new Map<LayerKey, LayerFeedDown>();
  for (const [layerKey, specs] of specsByLayer) {
    let allStale = true;
    let everReported = false;
    let downSinceMs = -Infinity;
    for (const spec of specs) {
      const lastPolledAtMs = lastPolledByWorker.get(spec.worker) ?? null;
      const state = workerLiveness(lastPolledAtMs, nowMs, feedHealthDownAfterMs(spec.expectedIntervalMinutes));
      if (state === 'live') {
        allStale = false;
        break;
      }
      if (lastPolledAtMs !== null) {
        everReported = true;
        if (lastPolledAtMs > downSinceMs) downSinceMs = lastPolledAtMs;
      }
    }
    if (allStale && everReported) {
      down.set(layerKey, {
        layerKey,
        downSinceMs,
        staleWorkers: specs.map((s) => s.worker),
      });
    }
  }
  return down;
}

/** Set of layer keys referenced by the registry (for tests / introspection). */
export function feedHealthLayerKeys(): LayerKey[] {
  return [...new Set(FEED_HEALTH_REGISTRY.map((s) => s.layerKey))];
}

/** True if `k` is a layer the registry can assess (used to guard chrome lookups). */
export function isFeedHealthLayer(k: string): boolean {
  return FEED_HEALTH_REGISTRY.some((s) => s.layerKey === k);
}

/** Guard: every registry layerKey is a real LAYER_KEYS member. Exposed so a unit
 *  test can pin the registry against the layer contract (drift = CI failure). */
export const FEED_HEALTH_VALID_LAYER_KEYS: ReadonlySet<string> = new Set(LAYER_KEYS);

/* ------------------------------------------------------------------------- *
 *  Provincial fire-feed PER-PROVINCE staleness (#603).
 *
 *  The reported-wildfire layer's all-backing-workers-stale down semantic is too
 *  coarse for a ONE-province outage: if BC's feed dies while CWFIS + AB/ON/QC stay
 *  live, the layer is (correctly) still "live" — but BC provincial fires are now
 *  MISSING, and an empty BC must read "provincial data unavailable", never "0
 *  fires" (the #337 unknown-≠-zero discipline, applied per province). This is a
 *  web-side MIRROR of the provincial-fires worker's PROVINCES config, drift-pinned
 *  to the worker (feed-health.provincial.drift.test.ts) so it can never diverge.
 * ------------------------------------------------------------------------- */

/** One provincial fire feed: its two-letter province, its worker_health worker,
 *  and the plain-language authority name for the "…unavailable" copy. */
export interface ProvincialFireFeedSpec {
  province: 'BC' | 'AB' | 'ON' | 'QC';
  worker: string;
  /** Human authority name (matches the ingestion attribution). */
  displayName: string;
}

/** The four provincial fire feeds — MIRROR of apps/ingestion provincial-fires
 *  PROVINCES (source→worker) + attribution authority. Drift-pinned to the worker. */
export const PROVINCIAL_FIRE_FEEDS: ReadonlyArray<ProvincialFireFeedSpec> = [
  { province: 'BC', worker: 'provincial-bc', displayName: 'BC Wildfire Service' },
  { province: 'AB', worker: 'provincial-ab', displayName: 'Alberta Wildfire' },
  { province: 'ON', worker: 'provincial-on', displayName: 'Ontario AFFES' },
  { province: 'QC', worker: 'provincial-qc', displayName: 'SOPFEU' },
];

/** Nominal provincial poll cadence (min) — matches DEFAULT_POLL_INTERVAL_S (900s). */
export const PROVINCIAL_FIRE_EXPECTED_INTERVAL_MINUTES = 15;

/** One province's honest feed state. `down`/`unknown` carry the last heartbeat so
 *  the UI can say "unavailable since X" (down) vs assert nothing (unknown). */
export interface ProvincialFireFeedHealth extends ProvincialFireFeedSpec {
  state: FeedHealthState;
  /** Epoch ms of the last heartbeat, or null when the worker never reported. */
  lastPolledAtMs: number | null;
  /** #640 — epoch ms of the last poll that WROTE ≥1 fire row, or null if the feed
   *  has never produced one (the row-count analogue of lastOkAt). */
  lastNonZeroAtMs: number | null;
  /** #640 unknown-≠-zero honesty gate. TRUE when the feed is LIVE (fresh
   *  heartbeat) but has produced NO fire row within the presumed-dead window (or
   *  ever). A reachable feed reporting zero fires is UNKNOWN, not confirmed-zero —
   *  the operator cue must state that plainly instead of letting absence read as a
   *  healthy "no fires" (the exact way SOPFEU polled `ok` with 0 rows for weeks).
   *  NEVER true for a 'down'/'unknown' feed (those are their own honest states),
   *  and it NEVER blanks the map or suppresses CWFIS — it is a surfaced fact, not
   *  a data-suppression (fail LOUD, not fail SHUT). */
  reportingZero: boolean;
}

/**
 * Per-province liveness of the provincial fire feeds from the per-worker
 * heartbeats. Same conservative rule as {@link workerLiveness}: 'live' within the
 * presumed-dead window, 'down' past it (a real "unavailable since X"), 'unknown'
 * when the worker never reported (never fabricate a fault). The caller renders a
 * per-province "provincial data unavailable" cue ONLY for 'down' provinces — an
 * empty province whose feed is LIVE is a genuine "no provincial fires right now".
 *
 * #640 — a live feed can still be DARK: SOPFEU polled every 15 min with outcome
 * 'ok' yet wrote ZERO fire rows for weeks, and the down signal (heartbeat recency)
 * could never catch it. `reportingZero` closes that hole from the row-count axis
 * (`lastNonZeroAt`): a LIVE feed that has not produced a fire within the
 * presumed-dead window (or ever) is flagged so the operator sees "reporting 0
 * fires — not independently confirmed" instead of a silent healthy board. This is
 * derived (not a stored flag), exactly like the down signal, so worker_health
 * stays raw substrate.
 */
export function deriveProvincialFireStaleness(
  heartbeats: ReadonlyArray<WorkerHeartbeat>,
  nowMs: number,
): ProvincialFireFeedHealth[] {
  const lastByWorker = new Map<string, number | null>();
  const lastNonZeroByWorker = new Map<string, number | null>();
  for (const hb of heartbeats) {
    lastByWorker.set(hb.worker, hb.lastPolledAt ? Date.parse(hb.lastPolledAt) : null);
    lastNonZeroByWorker.set(hb.worker, hb.lastNonZeroAt ? Date.parse(hb.lastNonZeroAt) : null);
  }
  const downAfterMs = feedHealthDownAfterMs(PROVINCIAL_FIRE_EXPECTED_INTERVAL_MINUTES);
  return PROVINCIAL_FIRE_FEEDS.map((feed) => {
    const lastPolledAtMs = lastByWorker.get(feed.worker) ?? null;
    const lastNonZeroAtMs = lastNonZeroByWorker.get(feed.worker) ?? null;
    const state = workerLiveness(lastPolledAtMs, nowMs, downAfterMs);
    const reportingZero =
      state === 'live' && (lastNonZeroAtMs === null || nowMs - lastNonZeroAtMs > downAfterMs);
    return { ...feed, lastPolledAtMs, lastNonZeroAtMs, state, reportingZero };
  });
}
